import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, ChevronDown, ChevronUp, Clock, CalendarIcon, Check, ChevronsUpDown, Search, CopyPlus } from "lucide-react";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useTimezones } from "@/hooks/data/useTimezones";
import { Skeleton } from "@/components/ui/skeleton";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { format, eachDayOfInterval, parseISO } from "date-fns";
import { useAuth } from "@/contexts/AuthContext";
import { graphqlClient } from "@/services/core/graphqlClient";
import { CLINICS_QUERIES } from "@/services/resources/clinicsGquery";
import { API_CONFIG } from "@/config/envConfig";

// Interface for facility availability data from location state (flat structure from table)
interface FacilityAvailabilityData {
  facility_availability_id: number;
  available_from_date: string;
  available_to_date: string;
  clinic_facility_id: number;
  clinic_name: string;
  facility_name: string;
  // Optional nested structure (in case raw API data is passed)
  available_facility?: Array<{
    facility_name: string;
    clinic_details: {
      name: string;
      id: number;
    };
  }>;
}

// Schema and type definitions
  const formSchema = z.object({
    clinic: z.string().min(1, "Clinic is required"),
    facility: z.string().min(1, "Facility is required"),
  });

type FormData = z.infer<typeof formSchema>;

interface TimeSlot {
  id: string;
  fromTime: string;
  toTime: string;
  timezone: string;
  slotDurationMinutes: string;
  error?: string;
  visibilityWindowId?: number | null;
  availabilityDayTimeId?: number | null;
}

interface DayTimeSlotResponse {
  created_by: number;
  created_datetime: string;
  updated_by: number | null;
  updated_datetime: string | null;
  day_of_week: number;
  available_start_time: string;
  available_end_time: string;
  availability_day_time_id: number;
}

// GraphQL query for fetching day/time slots
const GET_FACILITY_DAY_TIME_SLOTS = `
  query GetFacilityDayTimeSlots($facilityAvailabilityId: Int!) {
    ref_facility_availability_day_time(
      where: {facility_availability_id: {_eq: $facilityAvailabilityId}, is_deleted: {_eq: false}}
    ) {
      created_by
      created_datetime
      updated_by
      updated_datetime
      day_of_week
      available_start_time
      available_end_time
      availability_day_time_id
    }
  }
`;

// Day of week mapping (1 = Monday, 7 = Sunday)
const DAY_OF_WEEK_MAP: Record<number, string> = {
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
  7: 'Sunday'
};

// Helper to convert 24-hour time (HH:MM:SS) to 12-hour format (H:MM AM/PM)
const convert24To12Hour = (time24: string): string => {
  if (!time24) return '';
  const [hours, minutes] = time24.split(':');
  const hour = parseInt(hours, 10);
  const period = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${hour12}:${minutes} ${period}`;
};

interface DaySlots {
  enabled: boolean;
  slots: TimeSlot[];
}

interface Clinic {
  clinic_id: number;
  clinic_name: string;
  patient_clinic_timezone: string;
}

interface Facility {
  clinic_facility_id: number;
  facility_name: string;
}

interface FacilityAvailabilityFormProps {
  initialData?: Partial<FormData>;
  isEdit?: boolean;
  facilityAvailabilityId?: number;
}

const FacilityAvailabilityForm = ({ initialData, isEdit = false, facilityAvailabilityId }: FacilityAvailabilityFormProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, hasuraToken, user } = useAuth();
  
  // Get facility availability data from location state for edit mode
  const facilityAvailabilityData = location.state?.facilityAvailability as FacilityAvailabilityData | undefined;
  
  // Fetch timezones
  const { timezones, loading: timezonesLoading } = useTimezones();
  
  // Clinics and Facilities state
  const [clinics, setClinics] = useState<Clinic[]>([]);
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [clinicsLoading, setClinicsLoading] = useState(false);
  const [facilitiesLoading, setFacilitiesLoading] = useState(false);
  const [selectedClinicId, setSelectedClinicId] = useState<number | null>(null);
  const [clinicSearchOpen, setClinicSearchOpen] = useState(false);
  const [editDataLoaded, setEditDataLoaded] = useState(false);
  
  const [selectedTimezone, setSelectedTimezone] = useState("US/Pacific");
  const [fromDate, setFromDate] = useState<Date | undefined>();
  const [toDate, setToDate] = useState<Date | undefined>();
  const [dateError, setDateError] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [appointmentSlots, setAppointmentSlots] = useState<Record<string, DaySlots>>({
    Monday: { enabled: false, slots: [{ id: "1", fromTime: "", toTime: "", timezone: "US/Pacific", slotDurationMinutes: "", availabilityDayTimeId: null }] },
    Tuesday: { enabled: false, slots: [{ id: "3", fromTime: "", toTime: "", timezone: "US/Pacific", slotDurationMinutes: "", availabilityDayTimeId: null }] },
    Wednesday: { enabled: false, slots: [{ id: "4", fromTime: "", toTime: "", timezone: "US/Pacific", slotDurationMinutes: "", availabilityDayTimeId: null }] },
    Thursday: { enabled: false, slots: [{ id: "5", fromTime: "", toTime: "", timezone: "US/Pacific", slotDurationMinutes: "", availabilityDayTimeId: null }] },
    Friday: { enabled: false, slots: [{ id: "6", fromTime: "", toTime: "", timezone: "US/Pacific", slotDurationMinutes: "", availabilityDayTimeId: null }] },
    Saturday: { enabled: false, slots: [{ id: "7", fromTime: "", toTime: "", timezone: "US/Pacific", slotDurationMinutes: "", availabilityDayTimeId: null }] },
    Sunday: { enabled: false, slots: [{ id: "8", fromTime: "", toTime: "", timezone: "US/Pacific", slotDurationMinutes: "", availabilityDayTimeId: null }] }
  });
  
  const [daysInRange, setDaysInRange] = useState<string[]>([]);
  const [collapsedDays, setCollapsedDays] = useState<Record<string, boolean>>({});

  // Generate time options
  const hourOptions = Array.from({ length: 12 }, (_, i) => ({
    value: String(i + 1),
    label: String(i + 1)
  }));

  const minuteOptions = [
    { value: '00', label: '00' },
    { value: '15', label: '15' },
    { value: '30', label: '30' },
    { value: '45', label: '45' }
  ];

  const periodOptions = [
    { value: 'AM', label: 'AM' },
    { value: 'PM', label: 'PM' }
  ];

  // Fetch clinics on mount
  useEffect(() => {
    const fetchClinics = async () => {
      if (!hasuraToken) return;
      
      setClinicsLoading(true);
      try {
        const response = await graphqlClient.query<{ pep_vw_available_clinics: Clinic[] }>(
          CLINICS_QUERIES.GET_AVAILABLE_CLINICS,
          {},
          hasuraToken
        );
        
        if (response.success && response.data?.pep_vw_available_clinics) {
          setClinics(response.data.pep_vw_available_clinics);
        }
      } catch (error) {
        console.error('Error fetching clinics:', error);
        toast.error('Failed to load clinics');
      } finally {
        setClinicsLoading(false);
      }
    };

    fetchClinics();
  }, [hasuraToken]);

  // Fetch facilities when clinic is selected
  useEffect(() => {
    const fetchFacilities = async () => {
      if (!hasuraToken || !selectedClinicId) {
        setFacilities([]);
        return;
      }
      
      setFacilitiesLoading(true);
      try {
        const response = await graphqlClient.query<{ emr_clinic_facility: Facility[] }>(
          CLINICS_QUERIES.GET_EMR_FACILITIES_BY_CLINIC,
          { clinicId: selectedClinicId },
          hasuraToken
        );
        
        if (response.success && response.data?.emr_clinic_facility) {
          setFacilities(response.data.emr_clinic_facility);
        } else {
          setFacilities([]);
        }
      } catch (error) {
        console.error('Error fetching facilities:', error);
        toast.error('Failed to load facilities');
        setFacilities([]);
      } finally {
        setFacilitiesLoading(false);
      }
    };

    fetchFacilities();
  }, [hasuraToken, selectedClinicId]);

  // Helper function to parse time string into components
  const parseTimeString = (timeStr: string) => {
    if (!timeStr) return { hour: '', minute: '', period: '' };
    const match = timeStr.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!match) return { hour: '', minute: '', period: '' };
    return {
      hour: match[1],
      minute: match[2],
      period: match[3].toUpperCase()
    };
  };

  // Helper function to construct time string from components
  const constructTimeString = (hour: string, minute: string, period: string) => {
    if (!hour || !minute || !period) return '';
    return `${hour}:${minute} ${period}`;
  };

  // Calculate days in range when dates change
  const calculateDaysInRange = (start: Date | undefined, end: Date | undefined) => {
    if (!start || !end) {
      setDaysInRange([]);
      return;
    }
    
    const days = eachDayOfInterval({ start, end });
    const dayNames = days.map(date => {
      const dayIndex = date.getDay();
      return ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][dayIndex];
    });
    
    const uniqueDays = Array.from(new Set(dayNames));
    setDaysInRange(uniqueDays);
  };

  useEffect(() => {
    calculateDaysInRange(fromDate, toDate);
  }, [fromDate, toDate]);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      clinic: initialData?.clinic || "",
      facility: initialData?.facility || "",
    },
  });

  // Load edit data when in edit mode - find clinic by matching clinic_name
  useEffect(() => {
    if (isEdit && facilityAvailabilityData && clinics.length > 0 && !editDataLoaded) {
      console.log('Edit mode - facilityAvailabilityData:', facilityAvailabilityData);
      console.log('Clinics loaded:', clinics.length);
      
      // Get clinic name from flat structure or nested structure
      const clinicName = facilityAvailabilityData.clinic_name || 
                         facilityAvailabilityData.available_facility?.[0]?.clinic_details?.name;
      
      if (clinicName) {
        // Find clinic by name match
        const matchedClinic = clinics.find(c => c.clinic_name === clinicName);
        console.log('Matched clinic:', matchedClinic, 'for name:', clinicName);
        
        if (matchedClinic) {
          // Set the clinic - this will trigger facilities fetch
          form.setValue("clinic", String(matchedClinic.clinic_id));
          setSelectedClinicId(matchedClinic.clinic_id);
        }
      }
      
      // Set dates using available_from_date and available_to_date
      if (facilityAvailabilityData.available_from_date) {
        console.log('Setting fromDate:', facilityAvailabilityData.available_from_date);
        setFromDate(parseISO(facilityAvailabilityData.available_from_date));
      }
      if (facilityAvailabilityData.available_to_date) {
        console.log('Setting toDate:', facilityAvailabilityData.available_to_date);
        setToDate(parseISO(facilityAvailabilityData.available_to_date));
      }
      
      setEditDataLoaded(true);
    }
  }, [isEdit, facilityAvailabilityData, clinics, editDataLoaded, form]);

  // Set facility value once facilities are loaded for edit mode
  useEffect(() => {
    if (isEdit && facilityAvailabilityData && facilities.length > 0 && editDataLoaded) {
      const clinicFacilityId = facilityAvailabilityData.clinic_facility_id;
      console.log('Setting facility - clinicFacilityId:', clinicFacilityId, 'facilities:', facilities);
      
      if (clinicFacilityId) {
        // Find if facility exists in the facilities list
        const facilityExists = facilities.some(f => f.clinic_facility_id === clinicFacilityId);
        console.log('Facility exists:', facilityExists);
        
        if (facilityExists) {
          form.setValue("facility", String(clinicFacilityId));
        }
      }
    }
  }, [isEdit, facilityAvailabilityData, facilities, editDataLoaded, form]);

  // Fetch day/time slots for edit mode
  useEffect(() => {
    const fetchDayTimeSlots = async () => {
      if (!isEdit || !facilityAvailabilityData?.facility_availability_id || !hasuraToken) return;
      
      try {
        console.log('Fetching day/time slots for facility_availability_id:', facilityAvailabilityData.facility_availability_id);
        
        const response = await graphqlClient.query<{ ref_facility_availability_day_time: DayTimeSlotResponse[] }>(
          GET_FACILITY_DAY_TIME_SLOTS,
          { facilityAvailabilityId: facilityAvailabilityData.facility_availability_id },
          hasuraToken
        );
        
        if (response.success && response.data?.ref_facility_availability_day_time) {
          const dayTimeSlots = response.data.ref_facility_availability_day_time;
          console.log('Fetched day/time slots:', dayTimeSlots);
          
          // Group slots by day_of_week
          const slotsByDay: Record<string, TimeSlot[]> = {};
          
          dayTimeSlots.forEach((slot, index) => {
            const dayName = DAY_OF_WEEK_MAP[slot.day_of_week];
            if (!dayName) return;
            
            if (!slotsByDay[dayName]) {
              slotsByDay[dayName] = [];
            }
            
            slotsByDay[dayName].push({
              id: `edit-${slot.availability_day_time_id}-${index}`,
              fromTime: convert24To12Hour(slot.available_start_time),
              toTime: convert24To12Hour(slot.available_end_time),
              timezone: selectedTimezone,
              slotDurationMinutes: '',
              availabilityDayTimeId: slot.availability_day_time_id
            });
          });
          
          console.log('Grouped slots by day:', slotsByDay);
          
          // Update appointmentSlots state
          setAppointmentSlots(prev => {
            const updated = { ...prev };
            Object.keys(updated).forEach(day => {
              if (slotsByDay[day] && slotsByDay[day].length > 0) {
                updated[day] = {
                  enabled: true,
                  slots: slotsByDay[day]
                };
              }
            });
            return updated;
          });
        }
      } catch (error) {
        console.error('Error fetching day/time slots:', error);
      }
    };
    
    fetchDayTimeSlots();
  }, [isEdit, facilityAvailabilityData?.facility_availability_id, hasuraToken, selectedTimezone]);

  const watchedClinic = form.watch("clinic");
  const watchedFacility = form.watch("facility");

  const isFormValid = (): boolean => {
    const hasClinic = !!watchedClinic && watchedClinic.trim().length > 0;
    const hasFacility = !!watchedFacility && watchedFacility.trim().length > 0;
    const hasFromDate = !!fromDate;
    const hasToDate = !!toDate;
    
    const hasValidSlot = Object.values(appointmentSlots).some(dayData => 
      dayData.enabled && dayData.slots.some(slot => 
        slot.fromTime && slot.toTime && !slot.error
      )
    );
    
    return hasClinic && hasFacility && hasFromDate && hasToDate && hasValidSlot;
  };

  const handleClinicChange = (value: string, skipFacilityReset = false) => {
    form.setValue("clinic", value);
    const clinicId = parseInt(value);
    setSelectedClinicId(clinicId);
    // Reset facility when clinic changes (unless loading edit data)
    if (!skipFacilityReset) {
      form.setValue("facility", "");
      setFacilities([]);
    }
  };

  const toggleDay = (day: string) => {
    if (!daysInRange.includes(day)) return;
    
    setAppointmentSlots(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        enabled: !prev[day].enabled
      }
    }));
    
    if (!appointmentSlots[day].enabled) {
      setCollapsedDays(prev => ({ ...prev, [day]: false }));
    }
  };

  const toggleCollapse = (day: string) => {
    if (!appointmentSlots[day].enabled) return;
    setCollapsedDays(prev => ({ ...prev, [day]: !prev[day] }));
  };

  const addSlot = (day: string) => {
    const existingSlots = appointmentSlots[day].slots;
    const incompleteSlot = existingSlots.find(slot => !slot.fromTime || !slot.toTime);
    
    if (incompleteSlot) {
      toast.error(`Please complete all time slot fields for ${day} before adding a new slot`);
      return;
    }

    const newId = `${day}-${Date.now()}`;
    setAppointmentSlots(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        slots: [...prev[day].slots, { 
          id: newId, 
          fromTime: "", 
          toTime: "", 
          timezone: selectedTimezone, 
          slotDurationMinutes: "",
          availabilityDayTimeId: null  // New slots have null ID
        }]
      }
    }));
  };

  const removeSlot = (day: string, slotId: string) => {
    setAppointmentSlots(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        slots: prev[day].slots.filter(s => s.id !== slotId)
      }
    }));
  };

  // Copy all slots from a day to all other days (only for create mode)
  const copySlotsToAllDays = (sourceDay: string) => {
    const validSlots = appointmentSlots[sourceDay].slots.filter(
      slot => slot.fromTime && slot.toTime && !slot.error
    );
    
    if (validSlots.length === 0) {
      toast.error("Please add at least one complete time slot before copying to all days");
      return;
    }

    // Get all visible days except the source day
    const otherDays = dayOrder.filter(day => daysInRange.includes(day) && day !== sourceDay);
    
    if (otherDays.length === 0) {
      toast.info("No other days available in the selected date range");
      return;
    }

    let totalCopied = 0;
    let totalSkipped = 0;

    setAppointmentSlots(prev => {
      const updated = { ...prev };
      
      otherDays.forEach(day => {
        validSlots.forEach(sourceSlot => {
          // Check if a slot with the same time already exists
          const existingSlot = updated[day].slots.find(
            slot => slot.fromTime === sourceSlot.fromTime && slot.toTime === sourceSlot.toTime
          );
          
          if (existingSlot) {
            totalSkipped++;
            return;
          }

          // Enable the day if not already enabled and add slot
          if (!updated[day].enabled) {
            updated[day] = {
              enabled: true,
              slots: [{
                id: Math.random().toString(),
                fromTime: sourceSlot.fromTime,
                toTime: sourceSlot.toTime,
                timezone: sourceSlot.timezone,
                slotDurationMinutes: sourceSlot.slotDurationMinutes,
                availabilityDayTimeId: null
              }]
            };
          } else {
            updated[day] = {
              ...updated[day],
              slots: [
                ...updated[day].slots,
                {
                  id: Math.random().toString(),
                  fromTime: sourceSlot.fromTime,
                  toTime: sourceSlot.toTime,
                  timezone: sourceSlot.timezone,
                  slotDurationMinutes: sourceSlot.slotDurationMinutes,
                  availabilityDayTimeId: null
                }
              ]
            };
          }
          totalCopied++;
        });
      });

      return updated;
    });

    if (totalCopied > 0) {
      toast.success(`${totalCopied} slot(s) copied to other days${totalSkipped > 0 ? `, ${totalSkipped} skipped (duplicates)` : ''}`);
    } else if (totalSkipped > 0) {
      toast.info(`All days already have these time slots`);
    }
  };

  const checkSlotOverlap = (day: string, slotId: string, fromTime: string, toTime: string, slotsToCheck?: TimeSlot[]): string | null => {
    const parseTime = (timeStr: string): number => {
      const match = timeStr.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
      if (!match) return -1;
      let hours = parseInt(match[1]);
      const minutes = parseInt(match[2]);
      const period = match[3].toUpperCase();
      if (period === 'PM' && hours !== 12) hours += 12;
      if (period === 'AM' && hours === 12) hours = 0;
      return hours * 60 + minutes;
    };

    const newFrom = parseTime(fromTime);
    const newTo = parseTime(toTime);
    
    if (newFrom === -1 || newTo === -1) return null;
    if (newFrom >= newTo) return "End time must be after start time";

    const slots = slotsToCheck || appointmentSlots[day].slots;
    for (const slot of slots) {
      if (slot.id === slotId) continue;
      if (!slot.fromTime || !slot.toTime) continue;
      
      const existingFrom = parseTime(slot.fromTime);
      const existingTo = parseTime(slot.toTime);
      
      if (existingFrom === -1 || existingTo === -1) continue;
      if (newFrom < existingTo && newTo > existingFrom) {
        return "Time slot overlaps with an existing slot";
      }
    }
    
    return null;
  };

  const updateSlot = (day: string, slotId: string, field: 'fromTime' | 'toTime', value: string) => {
    const updatedSlots = appointmentSlots[day].slots.map(slot => {
      if (slot.id !== slotId) return slot;
      
      const newSlot = { ...slot, [field]: value };
      
      if (newSlot.fromTime && newSlot.toTime) {
        const error = checkSlotOverlap(day, slotId, newSlot.fromTime, newSlot.toTime);
        return { ...newSlot, error: error || undefined };
      }
      
      return newSlot;
    });

    setAppointmentSlots(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        slots: updatedSlots
      }
    }));
  };

  // Helper function to convert 12-hour time to 24-hour format (HH:mm:ss)
  const convertTo24HourFormat = (time12h: string): string => {
    const match = time12h.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!match) return '00:00:00';
    
    let hours = parseInt(match[1]);
    const minutes = match[2];
    const period = match[3].toUpperCase();
    
    if (period === 'PM' && hours !== 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;
    
    return `${hours.toString().padStart(2, '0')}:${minutes}:00`;
  };

  // Map day name to day of week number (1=Monday, 7=Sunday)
  const dayNameToNumber: Record<string, number> = {
    Monday: 1,
    Tuesday: 2,
    Wednesday: 3,
    Thursday: 4,
    Friday: 5,
    Saturday: 6,
    Sunday: 7
  };

  const onSubmit = async (values: FormData) => {
    if (!fromDate) {
      toast.error("From Date is required");
      return;
    }
    
    if (!toDate) {
      toast.error("End Date is required");
      return;
    }
    
    if (dateError) {
      toast.error("Please fix date validation errors");
      return;
    }
    
    const hasValidSlot = Object.values(appointmentSlots).some(dayData => 
      dayData.enabled && dayData.slots.some(slot => 
        slot.fromTime && slot.toTime && !slot.error
      )
    );
    
    if (!hasValidSlot) {
      toast.error("At least one time slot is required on any day");
      return;
    }

    if (!token || !user) {
      toast.error("Authentication required");
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      // Build dayTimes array from appointment slots with availabilityDayTimeId
      const dayTimes: { 
        dayOfWeek: number; 
        availableStartTime: string; 
        availableEndTime: string;
        availabilityDayTimeId?: number | null;
      }[] = [];
      
      Object.entries(appointmentSlots).forEach(([day, dayData]) => {
        if (dayData.enabled) {
          dayData.slots.forEach(slot => {
            if (slot.fromTime && slot.toTime && !slot.error) {
              dayTimes.push({
                dayOfWeek: dayNameToNumber[day],
                availableStartTime: convertTo24HourFormat(slot.fromTime),
                availableEndTime: convertTo24HourFormat(slot.toTime),
                availabilityDayTimeId: slot.availabilityDayTimeId || null
              });
            }
          });
        }
      });

      const payload: any = {
        clinicFacilityId: parseInt(values.facility),
        availableFromDate: format(fromDate, 'yyyy-MM-dd'),
        availableToDate: format(toDate, 'yyyy-MM-dd'),
        dayTimes
      };

      console.log('Submitting payload:', payload);

      // Use different endpoint for PUT (update) vs POST (create)
      const facilityAvailabilityId = facilityAvailabilityData?.facility_availability_id;
      const apiUrl = isEdit && facilityAvailabilityId
        ? `${API_CONFIG.API_BASE}/api/ngpep/${user.id}/appointment/${facilityAvailabilityId}`
        : `${API_CONFIG.API_BASE}/api/ngpep/${user.id}/appointment`;

      const response = await fetch(apiUrl, {
        method: isEdit ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (response.ok && result.success) {
        toast.success(result.message || (isEdit ? "Facility Availability updated successfully" : "Facility Availability created successfully"));
        navigate('/admin/facility-availability');
      } else {
        toast.error(result.message || (isEdit ? "Failed to update facility availability" : "Failed to create facility availability"));
      }
    } catch (error) {
      console.error('Error saving facility availability:', error);
      toast.error("Failed to save facility availability");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/facility-availability');
  };

  const dayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{isEdit ? "Edit Facility Availability" : "Add New Facility Availability"}</CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              {/* Basic Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                <FormField
                  control={form.control}
                  name="clinic"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <Label htmlFor="clinic">Select Clinic <span className="text-destructive">*</span></Label>
                      <Popover open={clinicSearchOpen} onOpenChange={setClinicSearchOpen}>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant="outline"
                              role="combobox"
                              aria-expanded={clinicSearchOpen}
                              className={cn(
                                "w-full justify-between bg-background",
                                !field.value && "text-muted-foreground"
                              )}
                              disabled={clinicsLoading}
                            >
                              {clinicsLoading 
                                ? "Loading clinics..." 
                                : field.value
                                  ? (clinics.find((clinic) => String(clinic.clinic_id) === field.value)?.clinic_name 
                                     || facilityAvailabilityData?.available_facility?.[0]?.clinic_details?.name
                                     || "Select Clinic")
                                  : "Select Clinic"}
                              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0 bg-background z-50" align="start">
                          <Command>
                            <CommandInput placeholder="Search clinic..." />
                            <CommandList>
                              <CommandEmpty>No clinic found.</CommandEmpty>
                              <CommandGroup>
                                {clinics.map((clinic) => (
                                  <CommandItem
                                    key={clinic.clinic_id}
                                    value={clinic.clinic_name}
                                    onSelect={() => {
                                      handleClinicChange(String(clinic.clinic_id));
                                      setClinicSearchOpen(false);
                                    }}
                                  >
                                    <Check
                                      className={cn(
                                        "mr-2 h-4 w-4",
                                        field.value === String(clinic.clinic_id) ? "opacity-100" : "opacity-0"
                                      )}
                                    />
                                    {clinic.clinic_name}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="facility"
                  render={({ field }) => {
                    // Get display value for the facility
                    const selectedFacility = facilities.find(f => String(f.clinic_facility_id) === field.value);
                    const displayValue = selectedFacility?.facility_name 
                      || (field.value && facilityAvailabilityData?.available_facility?.[0]?.facility_name)
                      || null;
                    
                    return (
                    <FormItem>
                      <Label htmlFor="facility">Select Facility Name <span className="text-destructive">*</span></Label>
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                        disabled={facilitiesLoading || !selectedClinicId}
                      >
                        <FormControl>
                          <SelectTrigger className="bg-background">
                            {displayValue ? (
                              <span>{displayValue}</span>
                            ) : (
                              <SelectValue placeholder={
                                !selectedClinicId 
                                  ? "Select a clinic first" 
                                  : facilitiesLoading 
                                    ? "Loading facilities..." 
                                    : "Select Facility Name"
                              } />
                            )}
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent className="bg-background z-50">
                          {facilities.map((facility) => (
                            <SelectItem key={facility.clinic_facility_id} value={String(facility.clinic_facility_id)}>
                              {facility.facility_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}}
                />
              </div>

              {/* Date Range */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>From Date <span className="text-destructive">*</span></Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !fromDate && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {fromDate ? format(fromDate, "MM/dd/yyyy") : "Select date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-background z-50" align="start">
                      <Calendar
                        mode="single"
                        selected={fromDate}
                        onSelect={(date) => {
                          setFromDate(date);
                          if (date && toDate && date > toDate) {
                            setDateError("From date cannot be after end date");
                          } else {
                            setDateError("");
                          }
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label>To Date <span className="text-destructive">*</span></Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !toDate && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {toDate ? format(toDate, "MM/dd/yyyy") : "Select date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-background z-50" align="start">
                      <Calendar
                        mode="single"
                        selected={toDate}
                        onSelect={(date) => {
                          setToDate(date);
                          if (fromDate && date && fromDate > date) {
                            setDateError("End date cannot be before from date");
                          } else {
                            setDateError("");
                          }
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label>Timezone</Label>
                  {timezonesLoading ? (
                    <Skeleton className="h-10 w-full" />
                  ) : (
                    <Select value={selectedTimezone} onValueChange={setSelectedTimezone}>
                      <SelectTrigger className="bg-background">
                        <SelectValue placeholder="Select timezone" />
                      </SelectTrigger>
                      <SelectContent className="bg-background z-50">
                        {timezones.map((tz) => (
                          <SelectItem key={tz.timezone} value={tz.timezone}>
                            {tz.timezone}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>

              {dateError && (
                <p className="text-sm text-destructive">{dateError}</p>
              )}

              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-muted-foreground" />
                  <h3 className="text-lg font-semibold">Facility Schedule</h3>
                </div>
                
                {daysInRange.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Select a date range to configure time slots</p>
                ) : (
                  <div className="space-y-3">
                    {dayOrder.filter(day => daysInRange.includes(day)).map((day) => (
                      <Collapsible
                        key={day}
                        open={appointmentSlots[day].enabled && !collapsedDays[day]}
                        onOpenChange={() => toggleCollapse(day)}
                      >
                        <div className="border rounded-lg">
                          <div className="flex items-center justify-between p-3 bg-muted/30">
                            <div className="flex items-center gap-3">
                              <input
                                type="checkbox"
                                checked={appointmentSlots[day].enabled}
                                onChange={() => toggleDay(day)}
                                className="h-4 w-4"
                              />
                              <span className="font-medium">{day}</span>
                              {appointmentSlots[day].enabled && (
                                <Badge variant="secondary" className="text-xs">
                                  {appointmentSlots[day].slots.filter(s => s.fromTime && s.toTime).length} slot(s)
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {/* Copy to All Days button - only for first day with valid slots in create mode */}
                              {!isEdit && appointmentSlots[day].enabled && 
                               appointmentSlots[day].slots.some(slot => slot.fromTime && slot.toTime && !slot.error) &&
                               dayOrder.filter(d => daysInRange.includes(d))[0] === day && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          copySlotsToAllDays(day);
                                        }}
                                        className="h-8 px-2 hover:bg-primary/10 hover:text-primary"
                                      >
                                        <CopyPlus className="h-4 w-4 mr-1" />
                                        <span className="text-xs">Copy to All Days</span>
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Copy all {day} slots to other days</TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                              {appointmentSlots[day].enabled && (
                                <CollapsibleTrigger asChild>
                                  <Button variant="ghost" size="sm">
                                    {collapsedDays[day] ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
                                  </Button>
                                </CollapsibleTrigger>
                              )}
                            </div>
                          </div>
                          
                          <CollapsibleContent>
                            <div className="p-4 space-y-3">
                              {appointmentSlots[day].slots.map((slot, index) => (
                                <div key={slot.id} className="flex items-center gap-3">
                                  <div className="flex items-center gap-2">
                                    <Select
                                      value={parseTimeString(slot.fromTime).hour}
                                      onValueChange={(hour) => {
                                        const { minute, period } = parseTimeString(slot.fromTime);
                                        updateSlot(day, slot.id, 'fromTime', constructTimeString(hour, minute || '00', period || 'AM'));
                                      }}
                                    >
                                      <SelectTrigger className="w-16 bg-background">
                                        <SelectValue placeholder="HH" />
                                      </SelectTrigger>
                                      <SelectContent className="bg-background z-50">
                                        {hourOptions.map(opt => (
                                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                    <span>:</span>
                                    <Select
                                      value={parseTimeString(slot.fromTime).minute}
                                      onValueChange={(minute) => {
                                        const { hour, period } = parseTimeString(slot.fromTime);
                                        updateSlot(day, slot.id, 'fromTime', constructTimeString(hour || '12', minute, period || 'AM'));
                                      }}
                                    >
                                      <SelectTrigger className="w-16 bg-background">
                                        <SelectValue placeholder="MM" />
                                      </SelectTrigger>
                                      <SelectContent className="bg-background z-50">
                                        {minuteOptions.map(opt => (
                                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                    <Select
                                      value={parseTimeString(slot.fromTime).period}
                                      onValueChange={(period) => {
                                        const { hour, minute } = parseTimeString(slot.fromTime);
                                        updateSlot(day, slot.id, 'fromTime', constructTimeString(hour || '12', minute || '00', period));
                                      }}
                                    >
                                      <SelectTrigger className="w-16 bg-background">
                                        <SelectValue placeholder="AM" />
                                      </SelectTrigger>
                                      <SelectContent className="bg-background z-50">
                                        {periodOptions.map(opt => (
                                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  
                                  <span className="text-muted-foreground">to</span>
                                  
                                  <div className="flex items-center gap-2">
                                    <Select
                                      value={parseTimeString(slot.toTime).hour}
                                      onValueChange={(hour) => {
                                        const { minute, period } = parseTimeString(slot.toTime);
                                        updateSlot(day, slot.id, 'toTime', constructTimeString(hour, minute || '00', period || 'AM'));
                                      }}
                                    >
                                      <SelectTrigger className="w-16 bg-background">
                                        <SelectValue placeholder="HH" />
                                      </SelectTrigger>
                                      <SelectContent className="bg-background z-50">
                                        {hourOptions.map(opt => (
                                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                    <span>:</span>
                                    <Select
                                      value={parseTimeString(slot.toTime).minute}
                                      onValueChange={(minute) => {
                                        const { hour, period } = parseTimeString(slot.toTime);
                                        updateSlot(day, slot.id, 'toTime', constructTimeString(hour || '12', minute, period || 'AM'));
                                      }}
                                    >
                                      <SelectTrigger className="w-16 bg-background">
                                        <SelectValue placeholder="MM" />
                                      </SelectTrigger>
                                      <SelectContent className="bg-background z-50">
                                        {minuteOptions.map(opt => (
                                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                    <Select
                                      value={parseTimeString(slot.toTime).period}
                                      onValueChange={(period) => {
                                        const { hour, minute } = parseTimeString(slot.toTime);
                                        updateSlot(day, slot.id, 'toTime', constructTimeString(hour || '12', minute || '00', period));
                                      }}
                                    >
                                      <SelectTrigger className="w-16 bg-background">
                                        <SelectValue placeholder="PM" />
                                      </SelectTrigger>
                                      <SelectContent className="bg-background z-50">
                                        {periodOptions.map(opt => (
                                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>

                                  {appointmentSlots[day].slots.length > 1 && (
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => removeSlot(day, slot.id)}
                                          >
                                            <Trash2 className="h-4 w-4 text-destructive" />
                                          </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>Remove slot</TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  )}
                                </div>
                              ))}
                              
                              {appointmentSlots[day].slots.some(s => s.error) && (
                                <p className="text-sm text-destructive">
                                  {appointmentSlots[day].slots.find(s => s.error)?.error}
                                </p>
                              )}
                              
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => addSlot(day)}
                                className="mt-2"
                              >
                                <Plus className="h-4 w-4 mr-1" /> Add Time Slot
                              </Button>
                            </div>
                          </CollapsibleContent>
                        </div>
                      </Collapsible>
                    ))}
                  </div>
                )}
              </div>

              {/* Form Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button type="submit" disabled={!isFormValid() || isSubmitting}>
                  {isSubmitting ? "Saving..." : isEdit ? "Update Facility Availability" : "Create Facility Availability"}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
};

export default FacilityAvailabilityForm;
